import { useMemo, useEffect } from 'react'
import { useAuthStore, useRestaurantStore, useDelivery, DeliveryService } from '@restaurant/core'
import { apiClient, isAuthExpiredError } from '@restaurant/api-client'
import { safeGetLogger } from '@restaurant/logging'
import { isShowcase } from '../showcase/showcase'


/**
 * Hook to auto-fetch user addresses on authentication
 * Uses the shared useDelivery hook with web-specific services
 *
 * Backend endpoint: GET /users/<business_id>/delivery_addresses
 * Requires JWT auth token (user_id extracted from token by backend)
 */
export function useAddressesData() {
  const { user, token, isAuthenticated } = useAuthStore()
  const { currentRestaurant } = useRestaurantStore()

  // Create web-specific DeliveryService instance
  const deliveryService = useMemo(() => {
    // Set auth token on apiClient if available
    if (token?.accessToken) {
      apiClient.setToken(token.accessToken)
    }

    return new DeliveryService({
      apiClient,
    })
  }, [token?.accessToken])

  const { useUserAddresses } = useDelivery(deliveryService)

  // Get business ID from restaurant store. useUserAddresses' underlying
  // useQuery (packages/core/src/hooks/useDelivery.ts) is `enabled: !!businessId
  // && !!user?.id` — isShowcase() isn't importable there (packages must not
  // depend on an app-specific module), so forcing businessId undefined here
  // disables the query via its existing `enabled` condition instead.
  const businessId = isShowcase() ? undefined : currentRestaurant?.id

  // Auto-fetch addresses when authenticated and we have a business ID
  const { data, isLoading, error, isFetching } = useUserAddresses(businessId)

  // Log authentication and fetch state
  useEffect(() => {
    if (isShowcase()) return
    safeGetLogger()?.debug('useAddressesData state', { domain: 'delivery', isAuthenticated, userId: user?.id, businessId })
  }, [isAuthenticated, user?.id, businessId, token?.accessToken])

  // Log loading/fetching state
  useEffect(() => {
    if (isShowcase()) return
    if (isLoading || isFetching) {
      safeGetLogger()?.debug('Fetching addresses', { domain: 'delivery', businessId })
    }
  }, [isLoading, isFetching, businessId])

  // Log when addresses are loaded or error occurs
  useEffect(() => {
    if (isShowcase()) return
    if (error) {
      if (isAuthExpiredError(error)) {
        // Auth refresh already failed; user is logged out. Auth flow handles recovery.
        safeGetLogger()?.warn('Address fetch failed — auth expired', { domain: 'delivery' })
      } else {
        safeGetLogger()?.error('Address fetch failed', { domain: 'delivery', error: error.message })
      }
    } else if (data) {
      safeGetLogger()?.debug('Addresses loaded', { domain: 'delivery', count: data.length })
    }
  }, [data, error])

  return { addresses: data, isLoading, error: error?.message || null }
}
